import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  approveTypeSetAside,
  listTypeSetAsideItems,
  rejectTypeSetAside,
  type TypeSetAsideItem,
} from '@/services/googleAdsMonthlyService';

export const TYPE_SET_ASIDE_QUERY_KEY = 'google-ads-type-set-aside';

function usd(micros?: number | null): string {
  if (micros == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(micros / 1_000_000);
}

/**
 * Budget suggestions Core cannot answer on its own: an in-account campaign
 * type (LSA) with a budget but no spend. Core sets aside the overdelivered
 * budget and asks a person to check the profile and type an amount. Approve
 * writes the Planner line and the campaign budget; Reject closes it for 30
 * days. Lives on Management; Budget pacing only points here.
 */
export function TypeSetAsideQueue({ tenantId, operatorEmail }: { tenantId: string; operatorEmail?: string }) {
  const queryClient = useQueryClient();
  const items = useQuery({
    queryKey: [TYPE_SET_ASIDE_QUERY_KEY, tenantId],
    queryFn: () => listTypeSetAsideItems(tenantId),
    enabled: Boolean(tenantId),
    refetchInterval: 30_000,
  });
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [TYPE_SET_ASIDE_QUERY_KEY, tenantId] });

  const approve = useMutation({
    mutationFn: (item: TypeSetAsideItem) =>
      approveTypeSetAside({
        tenantId,
        requestId: item.id,
        monthlyBudgetDollars: Number.parseFloat((amounts[item.id] ?? '').replace(/[$,]/g, '')),
        resolvedBy: operatorEmail,
      }),
    onSuccess: (res) => {
      toast.success(`Planner line ${res.planner.action}: $${res.planner.monthly_budget_dollars.toFixed(2)}/month`);
      invalidate();
    },
    onError: (err: unknown) => toast.error(`Could not approve: ${err instanceof Error ? err.message : String(err)}`),
  });
  const reject = useMutation({
    mutationFn: (item: TypeSetAsideItem) => rejectTypeSetAside({ tenantId, requestId: item.id, resolvedBy: operatorEmail }),
    onSuccess: () => {
      toast.success('Closed. Core will not ask again for 30 days.');
      invalidate();
    },
    onError: (err: unknown) => toast.error(`Could not reject: ${err instanceof Error ? err.message : String(err)}`),
  });

  const rows = items.data ?? [];
  if (rows.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="type-set-aside-queue">
      <div className="px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Budget suggestions
      </div>
      {rows.map((item) => {
        const raw = amounts[item.id] ?? '';
        const parsed = Number.parseFloat(raw.replace(/[$,]/g, ''));
        const valid = Number.isFinite(parsed) && parsed > 0;
        return (
          <div key={item.id} className="rounded-md border border-border bg-warm-bg px-4 py-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{item.payload.title ?? `${item.channel} needs a budget decision`}</span>
              <Badge variant="outline">{item.channel === 'LOCAL_SERVICES' ? 'LSA, same account' : item.channel}</Badge>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Current budget {usd(item.payload.current_daily_budget_micros)}/day, set aside {usd(item.payload.set_aside_daily_micros)}/day
              {item.payload.overdelivery_multiplier ? ` (overdelivery seen at ${item.payload.overdelivery_multiplier}x)` : ''}
              {item.payload.monthly_google_budget_usd ? `; Google budget $${item.payload.monthly_google_budget_usd.toLocaleString()}/month` : ''}.
            </p>
            <p className="mt-1 text-xs">{item.payload.ask}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Input
                aria-label={`Monthly budget for ${item.channel}`}
                type="number"
                min={1}
                step={1}
                placeholder="Monthly budget, USD"
                value={raw}
                onChange={(event) => setAmounts((prev) => ({ ...prev, [item.id]: event.target.value }))}
                className="h-8 w-48 tabular-nums"
              />
              <Button
                type="button"
                size="sm"
                className="bg-coral hover:bg-coral-hover"
                disabled={!valid || approve.isPending}
                onClick={() => approve.mutate(item)}
              >
                Approve with this amount
              </Button>
              <Button type="button" size="sm" variant="outline" disabled={reject.isPending} onClick={() => reject.mutate(item)}>
                Reject
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
